<template>
  <div class="login min-h-screen bg-gradient-to-br from-yellow-300 via-orange-900 to-orange-900">
    <div class="min-h-screen flex items-center justify-center py-12 px-4 sm:px-6 lg:px-8">
      <div class="max-w-md w-full space-y-8 bg-white/10 backdrop-blur-md p-8 rounded-xl shadow-2xl border border-white/20">
        <div>
          <h2 class="mt-6 text-center text-3xl font-extrabold text-yellow-300 flex items-center justify-center">
            <span class="mr-3">⚔️</span>
            Spearhead League Manager
          </h2>
          <p class="mt-2 text-center text-sm text-yellow-300/70">
            Connectez-vous à votre compte
          </p>
        </div>
        <form class="mt-8 space-y-6" @submit.prevent="handleLogin">
          <div class="rounded-md shadow-sm -space-y-px">
            <div>
              <label for="username" class="sr-only">Nom d'utilisateur</label>
              <input
                id="username"
                v-model="user.username"
                type="text"
                required
                class="appearance-none rounded-t-md relative block w-full px-3 py-2 border border-yellow-300/20 bg-black/40 placeholder-white/50 text-white focus:outline-none focus:ring-purple-500 focus:border-purple-500 focus:z-10 sm:text-sm"
                placeholder="Nom d'utilisateur"
              />
            </div>
            <div>
              <label for="password" class="sr-only">Mot de passe</label>
              <input
                id="password"
                v-model="user.password"
                type="password"
                required
                class="appearance-none rounded-b-md relative block w-full px-3 py-2 border border-yellow-300/20 bg-black/40 placeholder-white/50 text-white focus:outline-none focus:ring-purple-500 focus:border-purple-500 focus:z-10 sm:text-sm"
                placeholder="Mot de passe"
              />
            </div>
          </div>

          <div>
            <button
              type="submit"
              class="group relative w-full flex justify-center py-2 px-4 border border-transparent text-sm font-medium rounded-md text-yellow-300 bg-orange-900 hover:bg-orange-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-yellow-500 transition-colors duration-200"
              :disabled="loading"
            >
              <span class="absolute left-0 inset-y-0 flex items-center pl-3">
                <span class="mr-2">{{ loading ? '⏳' : '🔑' }}</span>
              </span>
              {{ loading ? 'Connexion en cours...' : 'Se connecter' }}
            </button>
          </div>

          <div v-if="message" class="mt-4 p-4 rounded-md" :class="successful ? 'bg-green-500/20 text-green-200 border border-green-500/30' : 'bg-red-500/20 text-red-200 border border-red-500/30'">
            {{ message }}
          </div>

          <div class="text-center mt-4 space-y-2">
            <p class="text-sm text-white/70">
              Pas encore de compte ?
              <router-link 
                to="/register" 
                class="font-medium text-yellow-300 hover:text-yellow-300 transition-colors duration-200"
              >
                Créer un compte
              </router-link>
            </p>
            <p class="text-sm text-white/70">
              <button 
                @click="showForgotPasswordModal = true"
                class="font-medium text-yellow-300 hover:text-yellow-200 transition-colors duration-200 underline"
              >
                Mot de passe oublié ?
              </button>
            </p>
          </div>
        </form>
      </div>
    </div>

    <!-- Modal Mot de passe oublié -->
    <div v-if="showForgotPasswordModal" class="fixed inset-0 bg-black/50 backdrop-blur-sm flex items-center justify-center z-50 p-4">
      <div class="bg-gradient-to-br from-yellow-900/90 to-orange-900/90 backdrop-blur-md rounded-xl shadow-2xl border border-yellow-300/20 max-w-md w-full p-6">
        <div class="flex justify-between items-center mb-4">
          <h3 class="text-xl font-bold text-yellow-300 flex items-center">
            <span class="mr-2">🔑</span>
            Récupération de mot de passe
          </h3>
          <button 
            @click="closeForgotPasswordModal"
            class="text-yellow-300/70 hover:text-yellow-300 transition-colors duration-200"
          >
            <svg class="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M6 18L18 6M6 6l12 12"/>
            </svg>
          </button>
        </div>
        
        <p class="text-yellow-200/80 text-sm mb-4">
          Entrez votre adresse email et nous vous enverrons un lien pour réinitialiser votre mot de passe.
        </p>
        
        <form @submit.prevent="handleForgotPassword">
          <div class="mb-4">
            <label for="forgot-email" class="block text-sm font-medium text-yellow-200 mb-2">
              Adresse email
            </label>
            <input
              id="forgot-email"
              v-model="forgotPasswordEmail"
              type="email"
              required
              class="w-full px-3 py-2 border border-yellow-300/20 bg-black/40 placeholder-white/50 text-white rounded-md focus:outline-none focus:ring-2 focus:ring-yellow-500 focus:border-transparent"
              placeholder="votre@email.com"
            />
          </div>
          
          <div v-if="forgotPasswordMessage" class="mb-4 p-3 rounded-md" :class="forgotPasswordSuccess ? 'bg-green-500/20 text-green-200 border border-green-500/30' : 'bg-red-500/20 text-red-200 border border-red-500/30'">
            {{ forgotPasswordMessage }}
          </div>
          
          <div class="flex space-x-3">
            <button
              type="button"
              @click="closeForgotPasswordModal"
              class="flex-1 px-4 py-2 border border-yellow-300/30 text-yellow-300 rounded-md hover:bg-yellow-300/10 transition-colors duration-200"
            >
              Annuler
            </button>
            <button
              type="submit"
              :disabled="forgotPasswordLoading"
              class="flex-1 px-4 py-2 bg-gradient-to-r from-yellow-500 to-orange-600 text-black rounded-md hover:from-yellow-600 hover:to-orange-700 transition-all duration-200 font-medium disabled:opacity-50 disabled:cursor-not-allowed"
            >
              {{ forgotPasswordLoading ? 'Envoi...' : 'Envoyer' }}
            </button>
          </div>
        </form>
      </div>
    </div>
  </div>
</template>

<script>
export default {
  name: 'LoginView',
  data() {
    return {
      user: {
        username: '',
        password: ''
      },
      loading: false,
      message: '',
      successful: false,
      // Variables pour le mot de passe oublié
      showForgotPasswordModal: false,
      forgotPasswordEmail: '',
      forgotPasswordLoading: false,
      forgotPasswordMessage: '',
      forgotPasswordSuccess: false
    };
  },
  computed: {
    loggedIn() {
      return this.$store.state.auth.status.loggedIn;
    }
  },
  created() {
    if (this.loggedIn) {
      this.$router.push('/profile');
    }
  },
  methods: {
    handleLogin() {
      this.loading = true;
      this.message = '';
      this.$store.dispatch('auth/signin', this.user).then(
        () => {
          this.successful = true;
          this.message = 'Connexion réussie !';
          this.$router.push('/home');
        },
        error => {
          this.successful = false;
          this.message = (error.response && error.response.data && error.response.data.message) ||
            error.message ||
            error.toString();
        }
      ).finally(() => {
        this.loading = false;
      });
    },
    
    closeForgotPasswordModal() {
      this.showForgotPasswordModal = false;
      this.forgotPasswordEmail = '';
      this.forgotPasswordMessage = '';
      this.forgotPasswordSuccess = false;
    },
    
    async handleForgotPassword() {
      if (!this.forgotPasswordEmail) {
        this.forgotPasswordMessage = 'Veuillez entrer votre adresse email';
        this.forgotPasswordSuccess = false;
        return;
      }
      
      this.forgotPasswordLoading = true;
      this.forgotPasswordMessage = '';
      
      try {
        // Appel à l'API pour envoyer l'email de récupération
        const response = await fetch('http://localhost:8082/api/auth/forgot-password', {
          method: 'POST',
          headers: {
            'Content-Type': 'application/json',
          },
          body: JSON.stringify({
            email: this.forgotPasswordEmail
          })
        });
        
        if (response.ok) {
          this.forgotPasswordSuccess = true;
          this.forgotPasswordMessage = 'Un email de récupération a été envoyé à votre adresse email.';
          // Fermer le modal après 3 secondes
          setTimeout(() => {
            this.closeForgotPasswordModal();
          }, 3000);
        } else {
          const errorData = await response.json();
          this.forgotPasswordSuccess = false;
          this.forgotPasswordMessage = errorData.message || 'Erreur lors de l\'envoi de l\'email';
        }
      } catch (error) {
        this.forgotPasswordSuccess = false;
        this.forgotPasswordMessage = 'Erreur de connexion. Veuillez réessayer plus tard.';
        console.error('Erreur lors de la récupération de mot de passe:', error);
      } finally {
        this.forgotPasswordLoading = false;
      }
    }
  }
};
</script>

<style scoped>
.login {
  background-image: 
    radial-gradient(circle at 25% 25%, rgba(255, 255, 255, 0.1) 1px, transparent 1px),
    radial-gradient(circle at 75% 75%, rgba(255, 255, 255, 0.1) 1px, transparent 1px);
  background-size: 40px 40px;
}
</style> 